import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spinner } from '@nvision/ui';

import { InfiniteScroll } from '@/components/infinite-scroll';
import { Page } from '@/components/page';
import { NoData } from '@/modules/errors';

import { useContractSearchFilters, useContracts } from '../../hooks';
import { ContractsSearchFiltersForm, type FormRef } from '../forms/contracts-search-filters-form';
import { ContractCard } from './contract-card';
import { ContractsControls } from './contracts-controls';

import styles from './contracts.module.css';

const SCROLL_OFFSET = 200;

export const Contracts: React.FC = () => {
  const { clearFilters, customerKey, contractsSearchParams, formValues, isSearchSubmitted, setFilters } =
    useContractSearchFilters();
  const {
    data: contracts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
  } = useContracts(contractsSearchParams, isSearchSubmitted);
  const { t } = useTranslation();

  const formRef = useRef<FormRef>({} as FormRef);
  const prevCustomerKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevCustomerKeyRef.current !== null && prevCustomerKeyRef.current !== customerKey && customerKey) {
      formRef.current?.reset();
      clearFilters();
    }

    if (customerKey) {
      prevCustomerKeyRef.current = customerKey;
    }
  }, [customerKey, clearFilters]);

  return (
    <Page>
      <Page.SideBar>
        <Page.SideBar.Header title="Contract Search" />
        <Page.SideBar.Main>
          <ContractsSearchFiltersForm
            customerKey={customerKey}
            formRef={formRef}
            initialValues={formValues}
            onSubmit={setFilters}
          />
        </Page.SideBar.Main>
        <Page.SideBar.Footer>
          <Button secondary type="button" size="sm" onClick={() => formRef.current?.reset()}>
            {t('translation:default.buttons.clear')}
          </Button>

          <Button type="submit" size="sm" primary onPress={() => formRef.current?.submit()}>
            {t('translation:default.buttons.search')}
          </Button>
        </Page.SideBar.Footer>
      </Page.SideBar>
      <Page.Main>
        <ContractsControls />
        {isLoading || (isRefetching && !isFetchingNextPage) ? (
          <div className={styles.loading}>
            <Spinner size="lg" />
          </div>
        ) : contracts?.items && contracts?.items?.length > 0 ? (
          <InfiniteScroll
            className={styles.list}
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage}
            loadMore={fetchNextPage}
            scrollOffset={SCROLL_OFFSET}
          >
            {contracts?.items.map((contract) => (
              <ContractCard key={contract.key} contract={contract} />
            ))}
          </InfiniteScroll>
        ) : (
          <NoData description="No data to display. Please select the filters from the left panel." />
        )}
      </Page.Main>
    </Page>
  );
};
